package fr.arcplex.network.impl.legacy

import fr.arcplex.network.cache.apiCache
import fr.arcplex.network.http.*
import fr.arcplex.network.logging.Logger
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.timeout
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withPermit
import kotlinx.serialization.json.*

open class ApiClient(val baseUrl: String, allowInvalidCertificates: Boolean = false) {
    companion object {
        protected const val ContentType = "Content-Type"
        protected const val Accept = "Accept"
        protected const val JsonMediaType = "application/json"
        protected const val FormDataMediaType = "multipart/form-data"
        protected const val XmlMediaType = "application/xml"
        private val semaphore = Semaphore(8)

        fun buildCacheKey(url: String, headers: Map<String, String>): String {
            val headerPart = headers.entries.sortedBy { it.key }.joinToString(";") { (k, v) -> "$k=$v" }
            return if (headerPart.isEmpty()) url else "$url|$headerPart"
        }

        var defaultHeaders: Map<String, String> by ApplicationDelegates.setOnce(mapOf(ContentType to JsonMediaType, Accept to JsonMediaType))

        val jsonHeaders: Map<String, String> = mapOf(ContentType to JsonMediaType, Accept to JsonMediaType)
    }

    private val client: HttpClient = createNetworkHttpClient(allowInvalidCertificates) {
        install(ContentNegotiation) {
            json(Serializer.json)
        }
        install(HttpTimeout) {
            socketTimeoutMillis = 70_000
        }
    }

    suspend fun requestJson(requestConfig: RequestConfig, body: Any? = null): ApiInfrastructureResponse<JsonMap?> = semaphore.withPermit {
        val urlString = buildUrlString(requestConfig)
        val cacheKey = if (requestConfig.method == RequestMethod.GET) {
            buildCacheKey(urlString, requestConfig.headers)
        } else {
            null
        }

        if (cacheKey != null) {
            val cached = apiCache.get(cacheKey)
            if (cached != null) {
                try {
                    val element = Serializer.json.parseToJsonElement(cached) as? JsonObject
                        ?: throw IllegalStateException("Invalid cached entry")
                    return@withPermit Success<JsonMap>(JsonMap(element), 200, emptyMap()) as ApiInfrastructureResponse<JsonMap?>
                } catch (e: Exception) {
                    Logger.e("ApiClient", "cache re-parse failed, evicting: ${e.message}")
                    apiCache.remove(cacheKey)
                }
            }
        }

        val urlBuilder = URLBuilder(urlString)
        val response: HttpResponse = client.request(urlBuilder.build()) {
            method = HttpMethod.parse(requestConfig.method.name)
            headers {
                requestConfig.headers.forEach { (key, value) -> append(key, value.toString()) }
                defaultHeaders.forEach { (key, value) -> append(key, value) }
            }
            if (body != null) setBody(body)
        }

        return when (response.status.value) {
            in 200..299 -> {
                val data = response.body<JsonObject>()
                if (cacheKey != null) {
                    try {
                        apiCache.put(cacheKey, data.toString())
                    } catch (e: Exception) {
                        Logger.e("ApiClient", "cache write failed: ${e.message}")
                    }
                }
                Success<JsonMap>(JsonMap(data), response.status.value, response.headersMap) as ApiInfrastructureResponse<JsonMap?>
            }
            else -> handleErrors(response)
        }
    }

    suspend fun requestMap(requestConfig: RequestConfig, body: Any? = null): ApiInfrastructureResponse<Map<String, String?>?> = semaphore.withPermit {
        val urlBuilder = URLBuilder(buildUrlString(requestConfig))
        val response: HttpResponse = client.request(urlBuilder.build()) {
            method = HttpMethod.parse(requestConfig.method.name)
            headers {
                requestConfig.headers.forEach { (key, value) -> append(key, value.toString()) }
                defaultHeaders.forEach { (key, value) -> append(key, value) }
            }
            if (body != null) setBody(body)
        }

        return when (response.status.value) {
            in 200..299 -> {
                val data = response.body<Map<String, String?>>()
                Success<Map<String, String?>>(data, response.status.value, response.headersMap) as ApiInfrastructureResponse<Map<String, String?>?>
            }
            else -> handleErrors(response)
        }
    }

    private suspend inline fun <reified T : Any> request(requestConfig: RequestConfig, body: Any? = null): ApiInfrastructureResponse<T?>  = semaphore.withPermit {
        val urlBuilder = URLBuilder(buildUrlString(requestConfig))
        val response: HttpResponse = client.request(urlBuilder.build()) {
            method = HttpMethod.parse(requestConfig.method.name)
            headers {
                requestConfig.headers.forEach { (key, value) -> append(key, value.toString()) }
                defaultHeaders.forEach { (key, value) -> append(key, value) }
            }
            if (body != null) setBody(body)
        }

        val status = response.status.value
        val headersMap = response.headersMap

        return when (status) {
            in 200..299 -> Success<T>(response.body<T>(), status, headersMap) as ApiInfrastructureResponse<T?>
            in 300..399 -> Redirection<T>(status, headersMap) as ApiInfrastructureResponse<T?>
            in 100..199 -> Informational<T>(response.status.description, status, headersMap) as ApiInfrastructureResponse<T?>
            in 400..499 -> ClientError<T>(response.bodyAsText(), status, headersMap) as ApiInfrastructureResponse<T?>
            else -> ServerError<T>(response.status.description, response.bodyAsText(), status, headersMap) as ApiInfrastructureResponse<T?>
        }
    }

    public fun buildUrlString(requestConfig: RequestConfig): String {
        if (requestConfig.fullURL != null) return requestConfig.fullURL!!
        val urlBuilder = URLBuilder(baseUrl.removeSuffix("/") + "/" + requestConfig.path.trimStart('/'))
        requestConfig.query.forEach { query ->
            query.value.forEach { queryValue -> urlBuilder.parameters.append(query.key, queryValue) }
        }
        return urlBuilder.buildString()
    }

    public suspend fun <T> handleErrors(response: HttpResponse): ApiInfrastructureResponse<T?> {
        val status = response.status.value
        val headersMap = response.headersMap
        return when (status) {
            in 400..499 -> ClientError<T>(response.bodyAsText(), status, headersMap) as ApiInfrastructureResponse<T?>
            else -> ServerError<T>(response.status.description, response.bodyAsText(), status, headersMap) as ApiInfrastructureResponse<T?>
        }
    }
}
